import { currentLoggedUserVar } from '@/shared/api';
import type { GetRPAgreementResponse } from '@/shared/api/graphql/administration';
import { GET_RELYING_PARTY_AGREEMENT } from '@/shared/api/graphql/administration';
import type { GetDocumentPreviewResponse } from '@/shared/api/graphql/rpmWorkflow';
import { GET_DOCUMENT_PREVIEW } from '@/shared/api/graphql/rpmWorkflow';
import { en } from '@/shared/lib/common/i18n';
import useToken from '@/shared/lib/hooks/useToken';
import type { Role } from '@/shared/lib/types/admin';
import { getIconDataBasedOnRole, getRoleTypeForCurrentUser } from '@/shared/lib/util/account';
import { stringAvatar } from '@/shared/lib/util/avatar';
import Button from '@/shared/ui/Button';
import { ProfileDialog } from '@/shared/ui/components/Dialogs';
import useSwitchCompaniesDialog from '@/shared/ui/components/Dialogs/Company/useSwitchCompaniesDialog';
import PdfDialog from '@/shared/ui/components/Dialogs/Documents/PdfDialog';
import MarkdownDialog from '@/shared/ui/components/Dialogs/MarkdownDialog';
import { ProfileAvatar } from '@/shared/ui/components/ProfileAvatar';
import RelyingAdminLogo from '@/shared/ui/components/RelyingAdminLogo';
import TruentityMenu from '@/shared/ui/components/TruentityMenu';
import { Body2, H3, H4 } from '@/shared/ui/components/Typography';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import type { MenuProps } from '@mui/material';
import { Box, Chip, ListItemButton, ListItemIcon, ListItemText, Stack, Typography } from '@mui/material';
import { useModal } from 'mui-modal-provider';
import { useSnackbar } from 'notistack';
import type { JSX, MouseEvent, ReactNode } from 'react';
import { useEffect, useState } from 'react';

const CustomMenuItem = ({ icon, onClick, label }: { icon: ReactNode; onClick: () => void; label: string }) => {
  return (
    <ListItemButton sx={{ color: 'inherit', fontWeight: '500', gap: 0.5, marginTop: 1.5 }} onClick={onClick}>
      <ListItemIcon sx={{ color: 'inherit', fontWeight: 'inherit' }}>{icon}</ListItemIcon>
      <ListItemText
        primary={label}
        slotProps={{
          primary: { sx: { fontWeight: 'inherit' } }
        }}
      />
    </ListItemButton>
  );
};
/** What a caller-supplied trigger gets to render with. */
export type ProfileMenuTriggerProps = {
  onClick: (event: MouseEvent<HTMLElement>) => void;
  open: boolean;
  name: string;
  company: string;
  avatarUrl?: string;
  roleTypeString: string;
  roleTypeIcon: ReactNode;
};

type Props = {
  /** Render a different trigger (e.g. the sidebar footer card) around the same profile menu. */
  renderTrigger?: (props: ProfileMenuTriggerProps) => ReactNode;
  anchorOrigin?: MenuProps['anchorOrigin'];
  transformOrigin?: MenuProps['transformOrigin'];
  /** Controlled open state, for a caller that has to open or close the menu from outside. */
  anchorEl?: HTMLElement | null;
  onAnchorElChange?: (anchorEl: HTMLElement | null) => void;
  /**
   * No backdrop: the rest of the page keeps receiving the pointer while the menu is open, and a
   * click anywhere outside it closes it.  For a menu opened on hover, whose surroundings have to
   * react to the pointer moving on.
   */
  nonBlocking?: boolean;
};

/** The popup's paper, found by id so the nav can measure it and test whether it is hovered. */
export const PROFILE_MENU_PAPER_ID = 'profile-menu-paper';

const ProfileMenu = ({
  renderTrigger,
  anchorOrigin,
  transformOrigin,
  anchorEl: controlledAnchorEl,
  onAnchorElChange,
  nonBlocking = false
}: Props = {}) => {
  const [ownAnchorEl, setOwnAnchorEl] = useState<null | HTMLElement>(null);
  const isControlled = controlledAnchorEl !== undefined;
  const anchorEl = isControlled ? controlledAnchorEl : ownAnchorEl;
  const setAnchorEl = (value: HTMLElement | null) => (isControlled ? onAnchorElChange?.(value) : setOwnAnchorEl(value));
  const open = Boolean(anchorEl);
  const currentUser = useReactiveVar(currentLoggedUserVar);
  const { handleLogout } = useToken();
  const [name, setName] = useState(' ');
  const [company, setCompany] = useState<string>(' ');
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);
  const [roleTypeString, setRoleTypeString] = useState('Unknown');
  const [roleTypeIcon, setRoleTypeIcon] = useState<JSX.Element | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { showModal } = useModal();

  const [getAgreement] = useLazyQuery<GetRPAgreementResponse>(GET_RELYING_PARTY_AGREEMENT);
  const [getDocumentPreview] = useLazyQuery<GetDocumentPreviewResponse>(GET_DOCUMENT_PREVIEW);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Without a backdrop there is nothing to click to dismiss, so listen for the click instead.
  useEffect(() => {
    if (!nonBlocking || !anchorEl) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (anchorEl.contains(target) || document.getElementById(PROFILE_MENU_PAPER_ID)?.contains(target)) return;
      if (isControlled) onAnchorElChange?.(null);
      else setOwnAnchorEl(null);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [nonBlocking, anchorEl, isControlled, onAnchorElChange]);

  const showProfileDialog = () => {
    handleClose();
    const modal = showModal(ProfileDialog, {
      title: en.administration.users.profileDialog.dialogTitle,
      hideDialog: () => {
        modal.hide();
      },
      pharmacistInfo: currentUser
    });
  };

  const showSwitchCompaniesDialog = useSwitchCompaniesDialog();

  const onAgreementClick = async () => {
    try {
      const response = await getAgreement();
      if (response?.data?.relyingPartyAgreement?.status === 'Success') {
        const agreementUrl = await fetch(`${response?.data?.relyingPartyAgreement?.agreementUrl}`);
        const blob = await agreementUrl.blob();
        const modal = showModal(PdfDialog, {
          title: <H3 sx={{ textAlign: 'center' }}>Terms of Agreement</H3>,
          pdfUrl: URL.createObjectURL(new Blob([blob], { type: 'application/pdf' })),
          hideDialog: () => {
            modal.hide();
          }
        });
      } else {
        throw new Error('Could not load Agreement');
      }
    } catch (err) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Could not load Agreement', { variant: 'error' });
    }
  };

  useEffect(() => {
    if (currentUser?.relyingParty) {
      const { Icon, color } = getIconDataBasedOnRole(currentUser.roleType as Role);

      const firstName = currentUser.user?.firstName || '';
      const lastName = currentUser.user?.lastName || '';
      setName(firstName + ' ' + lastName);
      setCompany(currentUser?.relyingParty?.name);
      setAvatarUrl(currentUser?.presignedAvatarUrl);
      setRoleTypeString(getRoleTypeForCurrentUser(currentUser));

      if (Icon) {
        setRoleTypeIcon(<Icon fontSize="small" color={color} />);
      }
    }
  }, [currentUser]);

  const fetchDocumentPreview = async () => {
    try {
      const documentResponse = await getDocumentPreview({
        variables: {
          documentS3Key: 'administration/releases/current.md'
        }
      });
      const url = `${documentResponse?.data?.documentPreview?.documentUrl}`;
      if (url) {
        const response = await fetch(url);
        const text = await response.text();
        const modal = showModal(MarkdownDialog, {
          title: 'Release Notes',
          text: text,
          hideDialog: () => {
            modal.hide();
          }
        });
      } else {
        enqueueSnackbar('Failed to load document preview', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar('Failed to load document preview', { variant: 'error' });
    }
  };

  return (
    <>
      {renderTrigger ? (
        renderTrigger({ onClick: handleClick, open, name, company, avatarUrl, roleTypeString, roleTypeIcon })
      ) : (
        <Box
          sx={{
            background: 'white',
            borderColor: 'grey.300',
            borderRadius: theme => theme.spacing(6),
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            paddingX: theme => theme.spacing(2),
            paddingY: theme => theme.spacing(1)
          }}
        >
          <Button
            variant={'text'}
            disableElevation
            onClick={handleClick}
            endIcon={<ProfileAvatar avatarUrl={avatarUrl} {...stringAvatar(name)} />}
          >
            <RelyingAdminLogo
              sx={{ maxWidth: '120px', height: 'auto', maxHeight: '56px', objectFit: 'contain', display: { xs: 'none', sm: 'block' } }}
            />
          </Button>
        </Box>
      )}

      <TruentityMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        {...(nonBlocking && {
          hideBackdrop: true,
          disableScrollLock: true,
          // The modal root still spans the screen; only the paper should catch the pointer.
          sx: { pointerEvents: 'none' }
        })}
        slotProps={{ paper: { id: PROFILE_MENU_PAPER_ID, sx: { pointerEvents: 'auto' } } }}
      >
        <Stack
          spacing={1}
          sx={{
            alignItems: 'center',
            color: 'primary.dark'
          }}
        >
          <ProfileAvatar avatarUrl={avatarUrl} name={name} />

          <Body2>{name}</Body2>
          <Chip
            variant="outlined"
            {...(roleTypeIcon && { icon: roleTypeIcon })}
            color="primary"
            label={roleTypeString}
            sx={{
              textTransform: 'capitalize',
              '& .MuiSvgIcon-root': {
                marginLeft: '12px'
              }
            }}
          />
          <RelyingAdminLogo sx={{ width: 'auto', maxWidth: 160, maxHeight: 40, objectFit: 'contain' }} />
          <H4 className="mt-3" sx={{ color: 'primary.main', letterSpacing: '0.06em' }}>
            {company}
          </H4>
          {currentUser?.hasMultipleRelyingParties ? (
            <Stack
              direction={'row'}
              sx={{
                padding: 0,
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CustomMenuItem icon={<ShuffleIcon />} onClick={() => showSwitchCompaniesDialog()} label={'SWITCH PHARMACY'} />
            </Stack>
          ) : (
            <></>
          )}
          <Stack
            direction={'row'}
            sx={{
              width: '100%',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CustomMenuItem icon={<PersonIcon />} onClick={() => showProfileDialog()} label={'PROFILE'} />
          </Stack>
          {/* The two references sit together, and signing out is last -- it is the one action
              here you would not want to hit on the way to something else. */}
          <Stack
            direction={'row'}
            sx={{
              width: '100%',
              alignItems: 'center',
              justifyContent: 'space-around'
            }}
          >
            <Typography
              color={'textPrimary'}
              sx={{
                textTransform: 'none'
              }}
              component={Button}
              onClick={fetchDocumentPreview}
              appearance={'plain'}
            >
              Release Notes
            </Typography>
            <Typography
              color={'textPrimary'}
              sx={{
                textTransform: 'none'
              }}
              component={Button}
              appearance={'plain'}
              onClick={onAgreementClick}
            >
              Terms
            </Typography>
          </Stack>
          <CustomMenuItem icon={<LogoutIcon />} onClick={() => handleLogout()} label={'SIGN OUT'} />
        </Stack>
      </TruentityMenu>
    </>
  );
};

export default ProfileMenu;
